import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  MessageSquare,
  Send,
  Users,
  FileText,
  Clock,
  XCircle,
  Plus,
  ArrowRight,
  Loader2,
  Repeat,
  Pause,
  Play,
  Gift
} from 'lucide-react';

const WADashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [fixedFlows, setFixedFlows] = useState([]);
  const [anniversaryFlows, setAnniversaryFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [isBlackoutToday, setIsBlackoutToday] = useState(false);

  useEffect(() => {
    fetchData();
    checkBlackout();
  }, []);

  const checkBlackout = async () => {
    try {
      const res = await api.get('/settings/wa-blackout-dates');
      const dates = res.data.dates || [];
      // Get today in MYT
      const now = new Date();
      const myt = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }));
      const todayStr = myt.getFullYear() + '-' + String(myt.getMonth() + 1).padStart(2, '0') + '-' + String(myt.getDate()).padStart(2, '0');
      setIsBlackoutToday(dates.includes(todayStr));
    } catch {}
  };

  const fetchData = async () => {
    try {
      const [statsRes, campaignsRes, flowsRes, annFlowsRes] = await Promise.all([
        api.get('/wa/dashboard'),
        api.get('/wa/campaigns'),
        api.get('/wa/fixed-flows').catch(() => ({ data: [] })),
        api.get('/wa/anniversary-flows').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data || []);
      setFixedFlows(flowsRes.data || []);
      setAnniversaryFlows(annFlowsRes.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePauseCampaign = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/campaigns/${id}/pause`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleResumeCampaign = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/campaigns/${id}/resume`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handlePauseFlow = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/fixed-flows/${id}/pause`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleResumeFlow = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/fixed-flows/${id}/resume`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handlePauseAnniversary = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/anniversary-flows/${id}/pause`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleResumeAnniversary = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/wa/anniversary-flows/${id}/resume`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // Merge campaigns + active fixed flows into a single sorted list (exclude completed)
  const unifiedItems = useMemo(() => {
    const campaignItems = campaigns
      .filter(c => c.status !== 'completed')
      .map(c => ({
      id: c.id,
      name: c.name,
      kind: c.type === 'scheduled' ? 'Scheduled' : 'Immediate (Ad Hoc)',
      itemType: 'campaign',
      status: c.status,
      progress: c.stats ? `${c.stats.sent}/${c.stats.total}` : '0/0',
      progressPct: c.stats?.total > 0 ? (c.stats.sent / c.stats.total) * 100 : 0,
      created_at: c.created_at,
      scheduledAt: c.scheduled_at || null,
      action: () => navigate(`/whatsapp/campaigns/${c.id}/queue`),
      actionLabel: c.status === 'paused' ? 'Paused' : c.status === 'running' ? 'Continue' : 'View Queue',
    }));

    const flowItems = fixedFlows.map(f => ({
      id: f.id,
      name: f.name,
      kind: 'Fixed Flow',
      itemType: 'flow',
      status: f.is_active ? 'active' : 'paused',
      progress: `${f.stats?.active || 0} active / ${f.stats?.total_clients || 0} total`,
      progressPct: f.stats?.total_clients > 0
        ? ((f.stats.completed || 0) / f.stats.total_clients) * 100
        : 0,
      created_at: f.created_at,
      scheduledAt: null,
      action: () => navigate(`/whatsapp/fixed-flows/${f.id}`),
      actionLabel: f.is_active ? 'View Queue' : 'Paused',
    }));

    const anniversaryItems = anniversaryFlows.map(f => ({
      id: f.id,
      name: f.name,
      kind: 'Anniversary',
      itemType: 'anniversary',
      status: f.is_active ? 'active' : 'paused',
      progress: `${f.stats?.sent || 0} sent / ${f.stats?.total_clients || 0} total`,
      progressPct: f.stats?.total_clients > 0
        ? ((f.stats.sent || 0) / f.stats.total_clients) * 100
        : 0,
      created_at: f.created_at,
      scheduledAt: null,
      action: () => navigate(`/whatsapp/anniversary-flows/${f.id}`),
      actionLabel: f.is_active ? 'View Queue' : 'Paused',
    }));

    return [...campaignItems, ...flowItems, ...anniversaryItems].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [campaigns, fixedFlows, anniversaryFlows, navigate]);

  const getTypeBadge = (kind) => {
    const map = {
      'Immediate (Ad Hoc)': 'bg-green-50 text-green-700 border border-green-200',
      Scheduled: 'bg-blue-50 text-blue-700 border border-blue-200',
      'Fixed Flow': 'bg-purple-50 text-purple-700 border border-purple-200',
      'Anniversary': 'bg-pink-50 text-pink-700 border border-pink-200',
    };
    return map[kind] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      ready: 'bg-indigo-100 text-indigo-700',
      running: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-purple-100 text-purple-700',
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-500',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const getTypeIcon = (kind) => {
    if (kind === 'Immediate (Ad Hoc)') return <Send size={14} className="text-green-600" />;
    if (kind === 'Scheduled') return <Clock size={14} className="text-blue-600" />;
    if (kind === 'Fixed Flow') return <Repeat size={14} className="text-purple-600" />;
    if (kind === 'Anniversary') return <Gift size={14} className="text-pink-600" />;
    return null;
  };

  // Format scheduled_at (stored as UTC) to Malaysia time
  const formatScheduledTime = (scheduledAt) => {
    if (!scheduledAt) return null;
    const utcStr = scheduledAt.endsWith('Z') ? scheduledAt : scheduledAt + 'Z';
    const date = new Date(utcStr);
    return date.toLocaleString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kuala_Lumpur'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wa-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Broadcast</h1>
          <p className="text-gray-500 mt-1">Manage your WhatsApp campaigns and messages</p>
        </div>
        <Button 
          data-testid="new-campaign-btn"
          onClick={() => navigate('/whatsapp/campaigns/new')}
          className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
        >
          <Plus size={16} className="mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Blackout Banner */}
      {isBlackoutToday && (
        <Card className="bg-red-50 border-red-200 p-4" data-testid="blackout-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Pause className="text-red-600" size={20} />
            </div>
            <div>
              <p className="font-semibold text-red-800">Blackout Day — All Broadcasts Paused</p>
              <p className="text-sm text-red-600">Today is a blackout date. All campaigns and fixed flows have been auto-paused and will resume automatically tomorrow.</p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto border-red-200 text-red-700 hover:bg-red-100" onClick={() => navigate('/settings')}>
              Manage Dates
            </Button>
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 bg-white border border-gray-100" data-testid="stat-messages-sent">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
              <Send className="text-green-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Messages Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_messages_sent || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100" data-testid="stat-pending">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center">
              <Clock className="text-yellow-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_messages_pending || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100" data-testid="stat-failed">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <XCircle className="text-red-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_messages_failed || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100" data-testid="stat-active-campaigns">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center">
              <MessageSquare className="text-purple-500" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.active_campaigns || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          data-testid="quick-action-templates"
          className="p-6 bg-white border border-gray-100 cursor-pointer hover:border-[#4F46E5] transition-colors"
          onClick={() => navigate('/whatsapp/templates')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="text-[#4F46E5]" size={24} />
              <div>
                <p className="font-medium text-gray-900">Templates</p>
                <p className="text-sm text-gray-500">{stats?.total_templates || 0} templates</p>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={20} />
          </div>
        </Card>

        <Card 
          data-testid="quick-action-groups"
          className="p-6 bg-white border border-gray-100 cursor-pointer hover:border-[#4F46E5] transition-colors"
          onClick={() => navigate('/whatsapp/groups')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="text-[#4F46E5]" size={24} />
              <div>
                <p className="font-medium text-gray-900">Client Groups</p>
                <p className="text-sm text-gray-500">{stats?.total_groups || 0} groups</p>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={20} />
          </div>
        </Card>

        <Card 
          data-testid="quick-action-campaigns"
          className="p-6 bg-white border border-gray-100 cursor-pointer hover:border-[#4F46E5] transition-colors"
          onClick={() => navigate('/whatsapp/campaigns')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Send className="text-[#4F46E5]" size={24} />
              <div>
                <p className="font-medium text-gray-900">All Campaigns</p>
                <p className="text-sm text-gray-500">{stats?.total_campaigns || 0} campaigns</p>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={20} />
          </div>
        </Card>

        <Card 
          data-testid="quick-action-fixed-flows"
          className="p-6 bg-white border border-gray-100 cursor-pointer hover:border-[#4F46E5] transition-colors"
          onClick={() => navigate('/whatsapp/fixed-flows')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Repeat className="text-[#4F46E5]" size={24} />
              <div>
                <p className="font-medium text-gray-900">Flows</p>
                <p className="text-sm text-gray-500">{fixedFlows.length} fixed flows</p>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={20} />
          </div>
        </Card>
      </div>

      {/* Unified Recent Campaigns Table */}
      <Card className="bg-white border border-gray-100" data-testid="recent-campaigns-table">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Campaigns</h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp/campaigns')}
            data-testid="view-all-campaigns-btn"
          >
            View All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {unifiedItems.length > 0 ? unifiedItems.slice(0, 10).map((item) => (
                <tr key={item.id} className="hover:bg-gray-50" data-testid={`campaign-row-${item.id}`}>
                  <td className="px-4 py-4">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    {item.kind === 'Scheduled' && item.scheduledAt && (
                      <p className="text-xs text-blue-600 mt-1">
                        <Clock size={12} className="inline mr-1" />
                        {formatScheduledTime(item.scheduledAt)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${getTypeBadge(item.kind)}`}>
                      {getTypeIcon(item.kind)} {item.kind}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadge(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {item.kind !== 'Fixed Flow' ? (
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[#25D366] h-2 rounded-full"
                            style={{ width: `${Math.min(item.progressPct, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{item.progress}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-600">{item.progress}</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant={item.status === 'paused' ? 'ghost' : 'outline'}
                        size="sm"
                        onClick={item.action}
                        data-testid={`action-btn-${item.id}`}
                        className={item.status === 'paused' ? 'text-yellow-600' : ''}
                      >
                        {item.actionLabel}
                      </Button>
                      {/* Pause/Resume button */}
                      {(item.status === 'running' || item.status === 'active' || item.status === 'scheduled') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (item.itemType === 'anniversary') handlePauseAnniversary(item.id);
                            else if (item.itemType === 'flow') handlePauseFlow(item.id);
                            else handlePauseCampaign(item.id);
                          }}
                          disabled={actionLoading[item.id]}
                          data-testid={`pause-btn-${item.id}`}
                          className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                        >
                          {actionLoading[item.id] ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />}
                        </Button>
                      )}
                      {item.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (item.itemType === 'anniversary') handleResumeAnniversary(item.id);
                            else if (item.itemType === 'flow') handleResumeFlow(item.id);
                            else handleResumeCampaign(item.id);
                          }}
                          disabled={actionLoading[item.id]}
                          data-testid={`resume-btn-${item.id}`}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          {actionLoading[item.id] ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No campaigns or flows yet. Create your first campaign to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default WADashboardPage;
